import React, { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps } from "react-router-dom";

// eslint-disable-next-line import/no-named-as-default
import ReCAPTCHA from "react-google-recaptcha";
import classNames from "classnames";

import { emailValidator } from "@/helper/validator";
import { postEmailForLogin } from "@/reducers/mrgReducer/mrgThunks";
import TextFields from "../../General/InputFields/TextFields";
import { AxiosError } from "axios";
import configs from "@/configs";

type OwnProps = {
  goNextPage: () => void;
};

type ReduxProps = ConnectedProps<typeof connector>;

type CombineProps = OwnProps & ReduxProps & RouteComponentProps;

type FormValues = {
  email: string;
};

type Props = CombineProps;

const PageOne: React.FC<Props> = ({ goNextPage, postEmailForLogin, email }) => {
  const {
    formState: { isValid, touched },
    errors,
    control,
    setError,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    mode: "all",
  });

  const watchedEmail = watch("email");
  useEffect(() => {
    if (watchedEmail === "iamadeveloper") {
      localStorage.setItem("@HUNGER/MERCHANT/DEBUG", "true");
      window.location.reload();
    }
  }, [watchedEmail]);

  const recaptchaRef = useRef<ReCAPTCHA>();

  const onSubmit = async (formValues: FormValues) => {
    try {
      if (!recaptchaRef.current) throw new Error("Recaptcha ref is empty");
      const token = await recaptchaRef.current.executeAsync();
      if (!token) throw new Error("Recaptcha token is empty");

      await postEmailForLogin(formValues.email, token);
      goNextPage();
    } catch (error) {
      recaptchaRef.current?.reset();
      const data = (error as AxiosError)?.response?.data;
      let message = "查無此Email，或Email有誤，請聯繫管理員。";

      if (data) {
        if ("message" in data) message = data.message;
        else if ("messages" in data) message = data.messages.join("\n");
      }

      if (data) {
        if (data.name !== "EMAIL_NOT_FOUND") {
          alert(`
            name: ${data.name}
            message:
            ----> ${data.messages.join("\n----> ")}
          `);
        }
      } else {
        alert(error);
      }
      setError("email", {
        type: "validate",
        message,
      });
    }
  };

  return (
    <main className="login__page">
      <form onSubmit={handleSubmit(onSubmit)} className="login__window">
        <h2 className="heading__2 login__window--heading">商家登入</h2>
        <p className="body login__window--body">親愛的夥伴，歡迎回來</p>
        <Controller
          name="email"
          control={control}
          render={(props) => (
            <TextFields
              error={errors.email?.message}
              touched={touched.email}
              label="電子信箱"
              inputMode="email"
              {...props}
            />
          )}
          rules={{ required: true, validate: emailValidator }}
          defaultValue={email}
        />
        <button
          disabled={!isValid}
          className="login__btn btn btn__primary"
          type="submit"
        >
          登入
        </button>
        <ReCAPTCHA
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ref={recaptchaRef}
          size={"invisible"}
          sitekey={configs.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
        />
        <div className={classNames("my-3")}>
          <p
            className={classNames(
              "mx-auto",
              "w-[16rem]",
              "text-grey-3 leading-4 text-[0.75rem]"
            )}
          >
            This site is protected by reCAPTCHA and the Google
            <a
              target={"_blank"}
              rel={"noreferrer noopener"}
              className={classNames("text-primary-pink mx-1")}
              href="https://policies.google.com/privacy"
            >
              Privacy Policy
            </a>{" "}
            and
            <a
              target={"_blank"}
              rel={"noreferrer noopener"}
              className={classNames("text-primary-pink mx-1")}
              href="https://policies.google.com/terms"
            >
              Terms of Service
            </a>{" "}
            apply.
          </p>
        </div>
        <p className="tiny login__apply">
          還不是會員嗎？{" "}
          <a
            href="https://hotcake.app"
            target="_blank"
            rel="noopener noreferrer"
            className="login__apply--anchor"
          >
            立即申請試用
          </a>
        </p>
      </form>
    </main>
  );
};

const mapStateToProps = (state: RootState) => ({
  email: state.mrg.email,
});

const mapDispatchToProps = { postEmailForLogin };

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(PageOne);
